/**
 * Question Type Selector Component
 *
 * Displays a grid of available question types for adding new questions.
 * Organized by category (text, choice, scale, special).
 */

import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  QuestionType,
  QuestionCategory,
  questionTypes,
  categoryLabels,
  categoryIcons,
  getQuestionTypesByCategory,
} from '@/lib/forms/question-types';

interface QuestionTypeSelectorProps {
  onSelect: (type: QuestionType) => void;
  disabled?: boolean;
  className?: string;
}

const categories: QuestionCategory[] = ['text', 'choice', 'scale', 'special'];

export function QuestionTypeSelector({
  onSelect,
  disabled,
  className,
}: QuestionTypeSelectorProps) {
  const [open, setOpen] = useState(false);
  const [hoveredType, setHoveredType] = useState<QuestionType | null>(null);

  const handleSelect = (type: QuestionType) => {
    onSelect(type);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn('gap-2', className)}
        >
          <Plus className="h-4 w-4" />
          Add Question
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Add Question</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          <div className="space-y-6">
            {categories.map(category => {
              const CategoryIcon = categoryIcons[category];
              const types = getQuestionTypesByCategory(category);

              return (
                <div key={category}>
                  <div className="flex items-center gap-2 mb-3">
                    <CategoryIcon className="h-4 w-4 text-muted-foreground" />
                    <h3 className="text-sm font-medium text-muted-foreground">
                      {categoryLabels[category]}
                    </h3>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {types.map(typeInfo => {
                      const Icon = typeInfo.icon;
                      const isHovered = hoveredType === typeInfo.type;

                      return (
                        <button
                          key={typeInfo.type}
                          type="button"
                          onClick={() => handleSelect(typeInfo.type)}
                          onMouseEnter={() => setHoveredType(typeInfo.type)}
                          onMouseLeave={() => setHoveredType(null)}
                          className={cn(
                            'flex flex-col items-start p-3 rounded-lg border text-left transition-all',
                            'hover:border-primary hover:bg-primary/5',
                            isHovered && 'border-primary bg-primary/5'
                          )}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <Icon className="h-4 w-4 text-primary" />
                            <span className="font-medium text-sm">
                              {typeInfo.label}
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground line-clamp-1">
                            {typeInfo.description}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Preview panel for hovered type */}
        {hoveredType && (
          <div className="border-t pt-4 mt-4">
            <div className="flex items-start gap-3">
              {(() => {
                const typeInfo = questionTypes.find(t => t.type === hoveredType);
                if (!typeInfo) return null;
                const Icon = typeInfo.icon;
                return (
                  <>
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium">{typeInfo.label}</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        {typeInfo.description}
                      </p>
                      <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                        {typeInfo.supportsOptions && (
                          <span className="flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                            Customizable options
                          </span>
                        )}
                        {typeInfo.supportsBranching && (
                          <span className="flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                            Supports branching
                          </span>
                        )}
                        {typeInfo.autoAdvance && (
                          <span className="flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                            Auto-advances
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Inline version for use in empty states
export function QuestionTypeSelectorInline({
  onSelect,
  className,
}: Omit<QuestionTypeSelectorProps, 'disabled'>) {
  return (
    <div className={cn('space-y-4', className)}>
      {categories.map(category => {
        const types = getQuestionTypesByCategory(category);

        return (
          <div key={category}>
            <h4 className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">
              {categoryLabels[category]}
            </h4>
            <div className="flex flex-wrap gap-2">
              {types.map(typeInfo => {
                const Icon = typeInfo.icon;
                return (
                  <button
                    key={typeInfo.type}
                    type="button"
                    onClick={() => onSelect(typeInfo.type)}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm',
                      'border bg-background hover:bg-muted transition-colors'
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {typeInfo.label}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
